import { getApiClient } from "../client";
import { SUBSCRIPTION_POPULATE } from "../populate";
import { FreePeriodPackageCreateResponseSchema, FreePeriodPackageInputSchema, FreePeriodPackageSchema, GrantSchema, StrapiIdSchema, SubscriptionQueryParamsSchema, SubscriptionSchema, SubscriptionTierInputSchema, SubscriptionTierListResponseSchema, SubscriptionTierSchema, UserMigrationInputSchema, validateInput, validateResponse, } from "../schemas";
import { parseStrapiItemResponse, parseStrapiListResponse, parseStrapiResponse } from "../utils/parseStrapiResponse";
export const subscriptionsEndpoints = {
    async getSubscription(id, params) {
        const validatedId = validateInput(StrapiIdSchema, id, "subscriptionsEndpoints.getSubscription");
        const populate = params?.populate ?? SUBSCRIPTION_POPULATE.DEFAULT;
        const response = await getApiClient().get(`/subscriptions/${validatedId}`, {
            params: { populate },
        });
        return parseStrapiItemResponse(response.data, {
            schema: SubscriptionSchema,
            populate,
            context: "subscriptionsEndpoints.getSubscription",
        });
    },
    async getSubscriptions(params) {
        const validated = params
            ? validateInput(SubscriptionQueryParamsSchema, params, "subscriptionsEndpoints.getSubscriptions")
            : undefined;
        const populate = params?.populate ?? SUBSCRIPTION_POPULATE.DEFAULT;
        const response = await getApiClient().get("/subscriptions", {
            params: {
                populate,
                ...validated,
            },
        });
        return parseStrapiListResponse(response.data, {
            schema: SubscriptionSchema,
            populate,
            context: "subscriptionsEndpoints.getSubscriptions",
        });
    },
    async createFreePeriodPackage(input) {
        const validated = validateInput(FreePeriodPackageInputSchema, input, "subscriptionsEndpoints.createFreePeriodPackage");
        const response = await getApiClient().post("/free-period-packages", validated);
        // The response is not wrapped in Strapi format, validate directly
        const validatedResponse = validateResponse(FreePeriodPackageCreateResponseSchema, response.data, {
            context: "subscriptionsEndpoints.createFreePeriodPackage",
        });
        return { data: validatedResponse, meta: {} };
    },
    async getPackagesByCredentials(credentialsId) {
        const validatedId = validateInput(StrapiIdSchema, credentialsId, "subscriptionsEndpoints.getPackagesByCredentials");
        const response = await getApiClient().get(`/free-period-packages/by-credentials/${validatedId}`);
        return parseStrapiListResponse(response.data, {
            schema: FreePeriodPackageSchema,
            context: "subscriptionsEndpoints.getPackagesByCredentials",
        });
    },
    async deactivatePackage(packageId) {
        const validatedId = validateInput(StrapiIdSchema, packageId, "subscriptionsEndpoints.deactivatePackage");
        const response = await getApiClient().delete(`/free-period-packages/${validatedId}`);
        return parseStrapiItemResponse(response.data);
    },
    async cancelSubscription(subscriptionId) {
        const validatedId = validateInput(StrapiIdSchema, subscriptionId, "subscriptionsEndpoints.cancelSubscription");
        const response = await getApiClient().put(`/subscriptions/${validatedId}/cancel`);
        return parseStrapiItemResponse(response.data, {
            schema: SubscriptionSchema,
            context: "subscriptionsEndpoints.cancelSubscription",
        });
    },
    /** Whether the failed charges of a subscription can be retried now (owner or admin). */
    async getRetryEligibility(subscriptionId) {
        const validatedId = validateInput(StrapiIdSchema, subscriptionId, "subscriptionsEndpoints.getRetryEligibility");
        const response = await getApiClient().get(`/subscriptions/${validatedId}/retry-eligibility`);
        return response.data.data;
    },
    /** Retry all failed charges now on the current card. Limited to once per day per subscription. */
    async retryFailedCharges(subscriptionId) {
        const validatedId = validateInput(StrapiIdSchema, subscriptionId, "subscriptionsEndpoints.retryFailedCharges");
        const response = await getApiClient().post(`/subscriptions/${validatedId}/retry-failed-charges`);
        return response.data.data;
    },
    async getGrantHistory(credentialsId) {
        const validatedId = validateInput(StrapiIdSchema, credentialsId, "subscriptionsEndpoints.getGrantHistory");
        const response = await getApiClient().get(`/subscriptions/${validatedId}/grant-history`);
        return parseStrapiListResponse(response.data, {
            schema: GrantSchema,
            context: "subscriptionsEndpoints.getGrantHistory",
        });
    },
    async adminExtendFree(subscriptionId, newPeriodEnd, reason) {
        const validatedId = validateInput(StrapiIdSchema, subscriptionId, "subscriptionsEndpoints.adminExtendFree");
        const response = await getApiClient().post(`/subscriptions/${validatedId}/admin-extend-free`, {
            newPeriodEnd,
            reason: reason ?? undefined,
        });
        return parseStrapiItemResponse(response.data, {
            schema: SubscriptionSchema,
            context: "subscriptionsEndpoints.adminExtendFree",
        });
    },
};
export const subscriptionTiersEndpoints = {
    async getAll() {
        const response = await getApiClient().get("/subscription-tiers");
        // The controller returns { data: tiers[], meta: { usersWithoutTier } }
        // Use parseStrapiResponse to flatten entities, then validate with the dedicated schema
        const { data, meta } = parseStrapiResponse(response.data);
        const dataArray = Array.isArray(data) ? data : [];
        return validateResponse(SubscriptionTierListResponseSchema, { data: dataArray, meta }, {
            context: "subscriptionTiersEndpoints.getAll",
        });
    },
    async getOne(id) {
        const validatedId = validateInput(StrapiIdSchema, id, "subscriptionTiersEndpoints.getOne");
        const response = await getApiClient().get(`/subscription-tiers/${validatedId}`);
        return parseStrapiItemResponse(response.data, {
            schema: SubscriptionTierSchema,
            context: "subscriptionTiersEndpoints.getOne",
        });
    },
    async create(input) {
        const validated = validateInput(SubscriptionTierInputSchema, input, "subscriptionTiersEndpoints.create");
        const response = await getApiClient().post("/subscription-tiers", {
            data: validated,
        });
        return parseStrapiItemResponse(response.data, {
            schema: SubscriptionTierSchema,
            context: "subscriptionTiersEndpoints.create",
        });
    },
    async update(id, input) {
        const validatedId = validateInput(StrapiIdSchema, id, "subscriptionTiersEndpoints.update");
        const validated = validateInput(SubscriptionTierInputSchema, input, "subscriptionTiersEndpoints.update");
        const response = await getApiClient().put(`/subscription-tiers/${validatedId}`, {
            data: validated,
        });
        return parseStrapiItemResponse(response.data, {
            schema: SubscriptionTierSchema,
            context: "subscriptionTiersEndpoints.update",
        });
    },
    async remove(id) {
        const validatedId = validateInput(StrapiIdSchema, id, "subscriptionTiersEndpoints.remove");
        const response = await getApiClient().delete(`/subscription-tiers/${validatedId}`);
        return parseStrapiItemResponse(response.data);
    },
    async setDefault(id) {
        const validatedId = validateInput(StrapiIdSchema, id, "subscriptionTiersEndpoints.setDefault");
        const response = await getApiClient().put(`/subscription-tiers/${validatedId}/set-default`);
        return parseStrapiItemResponse(response.data, {
            schema: SubscriptionTierSchema,
            context: "subscriptionTiersEndpoints.setDefault",
        });
    },
    async getUsersByTier(tierId, search) {
        const validatedId = validateInput(StrapiIdSchema, tierId, "subscriptionTiersEndpoints.getUsersByTier");
        const response = await getApiClient().get(`/subscription-tiers/${validatedId}/users`, {
            params: { search },
        });
        // No schema - custom response type
        return parseStrapiListResponse(response.data);
    },
    async migrateUsers(input) {
        const validated = validateInput(UserMigrationInputSchema, input, "subscriptionTiersEndpoints.migrateUsers");
        const response = await getApiClient().post(`/subscription-tiers/${validated.toTierId}/migrate-users`, {
            userIds: validated.userIds,
            fromTierId: validated.fromTierId,
        });
        return parseStrapiItemResponse(response.data);
    },
};
